import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import { stampAddToCartTime } from '@theme/line-item-properties';
import VariantPicker from '@theme/variant-picker';
import { getCheckedPurchaseOption, restorePurchaseOption } from '@theme/purchase-options';

/** Asks the open modal to re-render around a different product. @see QuickAddComponent#loadProduct */
export const QUICK_ADD_LOAD_PRODUCT = 'quickAdd:loadProduct';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** The product currently in the modal, which swatches can change. @type {string} */
  #openProductUrl = '';

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
    document.addEventListener(QUICK_ADD_LOAD_PRODUCT, this.#handleLoadProduct);
    // Capture, so the property is on the form before ProductFormComponent.handleSubmit
    // reads it into FormData on the bubble phase.
    this.addEventListener('submit', this.#handleFormSubmit, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
    document.removeEventListener(QUICK_ADD_LOAD_PRODUCT, this.#handleLoadProduct);
    this.removeEventListener('submit', this.#handleFormSubmit, { capture: true });
  }

  /**
   * Only the trigger that opened the modal owns its content.
   * @param {Event} event
   */
  #handleLoadProduct = (event) => {
    if (!this.hasAttribute('stay-visible')) return;

    const path = /** @type {CustomEvent} */ (event).detail?.path;
    if (path) this.loadProduct(path);
  };

  /**
   * Re-renders the open modal around a different product, keeping the shopper in it.
   * @param {string} path - The product page URL to load
   */
  async loadProduct(path) {
    const html = await this.fetchProductPage(path);
    const grid = html?.querySelector('[data-product-grid-content]');
    if (!grid) return;

    this.#openProductUrl = path;
    await this.updateQuickAddModal(/** @type {Element} */ (grid.cloneNode(true)));
  }

  /** @param {Event} event */
  #handleFormSubmit = (event) => {
    if (event.target instanceof HTMLFormElement) stampAddToCartTime(event.target);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    this.#openProductUrl = currentUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), {
      once: true,
    });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const productDetails = productGrid.querySelector('.product-details');
    const productInfo = productGrid.querySelector('.product-information__product-info');

    if (productInfo) this.#linkHeadingTitle(productInfo);

    if (isMobileBreakpoint()) {
      const lifted = [
        productGrid.querySelector('variant-picker'),
        productGrid.querySelector('.print-swatches-block') ?? productGrid.querySelector('ss-print-swatches'),
        productGrid.querySelector('.purchase-options-block') ?? productGrid.querySelector('product-form-component'),
        productGrid.querySelector('.product-oos-form-group'),
      ];

      for (const element of lifted) {
        if (element) productGrid.appendChild(element);
      }

      productDetails?.remove();
    } else if (productInfo && productDetails) {
      // Desktop: the heading has to scroll with the details column, so nest it there.
      const detailsContent = productDetails.querySelector('.group-block-content');
      detailsContent?.prepend(productInfo);
    }

    // The sibling product renders its own default-checked option, so a swatch swap
    // would otherwise drop the shopper back to Subscribe. Size survives because the
    // swap URL carries ?variant=; nothing carries this.
    const purchaseOption = getCheckedPurchaseOption(modalContent);

    morph(modalContent, productGrid);

    if (purchaseOption) restorePurchaseOption(modalContent, purchaseOption);

    // morph reports its post-update hook against the parsed node, and DOMParser
    // documents never upgrade custom elements — so a component patched in place is
    // never told. Re-render the ones that hold state by hand.
    for (const element of modalContent.querySelectorAll(
      'purchase-option-selector, ss-print-swatches, size-guide-tabs',
    )) {
      if (element instanceof Component) element.updatedCallback();
    }

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Links the heading's title to the product page. The PDP renders it unlinked, since
   * there the link would point at the page you are already on.
   * @param {Element} productInfo - The product info heading
   */
  #linkHeadingTitle(productInfo) {
    const title = productInfo.querySelector('.product-title-block');
    if (!title || title.closest('a')) return;

    const link = document.createElement('a');
    link.className = 'contents user-select-text';
    link.setAttribute('ref', 'productTitleLink');
    link.href = this.#openProductUrl || this.productPageUrl;

    title.replaceWith(link);
    link.appendChild(title);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const hotspot = this.closest('product-hotspot-component');
    if (hotspot) {
      return;
    }

    const productCard = this.closest('product-card');
    if (productCard) {
      if (event.target.closest('product-card') !== productCard) return;
    } else {
      const sectionScope = this.closest('featured-product-information, .shopify-section');
      if (sectionScope && event.target.closest('featured-product-information, .shopify-section') !== sectionScope) {
        return;
      }
    }

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const headingTitleLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.product-information__product-info a[ref="productTitleLink"]')
    );

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (headingTitleLink) headingTitleLink.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
